// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek
//
// this contains all volatile current state variables of connected site

import { IAlarmEntry } from "shared/types/globtypes";

// decoded live per-block status of one condition, see IStateData.conditionBlockStatus
export interface IStateConditionBlockStatus {
  conditionIndex: number;
  blockResult: number[];
  dataValue: number[];
  connectionStatus: number[];
}

export interface IStateDataOutput {
  state: number | undefined; // state bitfield
  logical: number; // numeric value
  physical: number; // numeric value
}

export interface IControlDataRow {
  timestamp: number;
  energy: number; // real energy, not as power unit
  isEstimate: boolean;
}

export interface ITariffCircuitData {
  timeOfPeriod: number;
  power: number;
  energy: number; // real energy, not as power unit
}

export interface IControlData {
  targetValue: number;
  regulationPriorityStatus: number;
  trendLevel: number;
  values: IControlDataRow[];
}

// keep consistent with chargectl/types.ts
export enum PlugState {
  no_cable,
  cable_in_charger,
  cable_in_charger_and_car,
}

// keep consistent with chargectl/types.ts
export enum ChargingState {
  not_ready,
  available,
  charging,
  charging_paused,
  error,
  charging_paused_by_EV,
}

// this is only used in local controller web interface
export interface IStateDataCharger {
  curP?: number;
  curI?: number[];
  curU?: number[];
  devMaxI?: number;
  setMaxI?: number;
  eTotal?: number;
  eSession?: number;
  plugState?: PlugState;
  plugIsLocked?: boolean;
  chargingState?: ChargingState;
  isConnected: boolean;
  chargerWritePending: boolean;
  errorChargerRW: boolean;
  auth?: string;
  reqMaxPower?: number;
  reqMaxCurrent?: number;
}

export interface IStateData {
  timestamp: number;

  input: number[];
  output: IStateDataOutput[];
  energymeter: number[];
  tariffcircuit: ITariffCircuitData[];
  controlcircuit: IControlData[];
  condition: number[];
  module: boolean[];

  charger?: IStateDataCharger[]; // only used in local controller web interface

  alarms: IAlarmEntry[];

  // live per-block status of the condition currently shown on the condition detail page,
  // read on demand while directly connected to the controller (see ConditionDetailPage)
  conditionBlockStatus?: IStateConditionBlockStatus;
}

/* initial state */
export const INITIAL_STATE: IStateData = {
  timestamp: 0,
  input: [],
  output: [],
  energymeter: [],
  tariffcircuit: [],
  controlcircuit: [],
  condition: [],
  module: [],
  alarms: [],
};
